import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { planService } from '../../application/PlanService';
import { stopService } from '../../application/StopService';
import * as routing from '../../constants/Routing';
import { Plan } from '../../domain/plan/Plan';
import { Project } from '../../domain/project/Project';
import { ProjectPlans } from '../../domain/project/ProjectPlans';
import { Stop } from '../../domain/stop/Stop';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../project/useRetrieveActiveProjectGeoFences';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUser } from '../user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';

type UseMoveStopsToANewPlanProps = {
  stopIds: string[];
};

type UseMoveStopsToANewPlanResponseProps = ServiceResponse<
  ({ stopIds }: UseMoveStopsToANewPlanProps) => void,
  undefined
>;

// Hooks act as 'Adapter' layer.
export const useMoveStopsToANewPlan = ({
  project,
  navigateToDestination,
}: {
  project?: Project;
  navigateToDestination: boolean;
}): UseMoveStopsToANewPlanResponseProps => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { data: user } = useRetrieveUser();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async ({ stopIds }: UseMoveStopsToANewPlanProps): Promise<Stop[]> => {
      const planLabel = ProjectPlans.labelTemplateToValue({
        template: activeProject!.plans.label,
        date: new Date(),
        language: user?.language.value,
      });

      const plan = Plan.create({
        label: planLabel,
        status: activeProject!.plans.status,
        execution_date: activeProject!.plans.defaultStartDateToValue(),
        project_id: project!.id,
        organization_id: organization!.id,
        geo_fences: userCredentials?.subscription.test(`planner.geofences`) ? projectGeoFences ?? [] : [],
      });
      const newPlan = await planService.create({ plan, project: activeProject! });

      return stopService.moveStopsToPlan({ stopIds, planId: newPlan.id, project: activeProject! });
    },
    {
      onSuccess: async (movedStops: Stop[]) => {
        movedStops.map(async (movedStop) => {
          await queryClient.invalidateQueries([`planner_stop`, movedStop.id]);
        });
        await queryClient.invalidateQueries([`planner_search_stops`]);

        if (navigateToDestination) {
          navigate(
            `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, `${activeProject!.id}`).replace(
              `:planId`,
              `${movedStops[0].plan_id}`
            )}`
          );
        }
      },
      onError: (err: any) => {
        console.info(`Move Stops To New Plan Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ stopIds }: UseMoveStopsToANewPlanProps) => mutate({ stopIds }),
  };
};
